import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser, loginUserWithGoogle } from "../apis/auth";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import {
  LogIn,
  AlertCircle,
  Shield,
  Eye,
  EyeOff,
  MessageSquare,
  Send,
  Zap,
  BarChart3,
  Sparkles,
  Smartphone,
  Code,
} from "lucide-react";
import bluTecPingLogo from "../assets/i1.png";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";

const Login = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "BluTec PING Login — Access Your WhatsApp Growth Dashboard";
    const description =
      "Securely sign in to BluTec PING to run WhatsApp broadcasts, automate chat, and track performance in real time.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const response = await loginUser(form);
      if (response.data.success === false) {
        throw new Error(response.data.error || "Login failed.");
      } else {
        localStorage.setItem("user", JSON.stringify(response.data.user));
        localStorage.setItem("user_id", JSON.stringify(response.data.user.id));
        localStorage.setItem("userAccessToken", response.data.token);
        toast.success(response.data.message || "Login successful!");
        setTimeout(() => navigate("/dashboard"), 1000);
      }
    } catch (err) {
      const message =
        err.response?.data?.error || err.message || "Login failed.";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    setIsGoogleLoading(true);
    try {
      const data = { token: credentialResponse.credential };
      const response = await loginUserWithGoogle(data);
      if (response.data.success) {
        localStorage.setItem("user", JSON.stringify(response.data.user));
        localStorage.setItem("user_id", JSON.stringify(response.data.user.id));
        localStorage.setItem("userAccessToken", response.data.token);
        toast.success(response.data.message || "Google sign-in successful!");
        setTimeout(() => navigate("/dashboard"), 1000);
      } else {
        toast.error(response.data.error || "Google Login failed.");
      }
    } catch (err) {
      toast.error("Google Login failed. Try again.");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const googleClientId = process.env.REACT_APP_CLIENT_ID;

  const shouldShowForgotPassword = () => {
    if (!error) return false;
    const lowered = error.toLowerCase();
    return ["password", "credential", "invalid"].some((k) =>
      lowered.includes(k)
    );
  };

  const features = [
    {
      icon: Smartphone,
      title: "Connect WhatsApp in 1 minute",
      description: "Quick and easy WhatsApp integration",
    },
    {
      icon: Code,
      title: "Send message using API",
      description: "Powerful API for seamless integration",
    },
    {
      icon: MessageSquare,
      title: "AI Chatbot",
      description: "Automate customer conversations",
    },
    {
      icon: Zap,
      title: "Fast & Reliable",
      description: "Lightning-fast message delivery",
    },
  ];

  return (
    <div className="h-screen w-full flex overflow-hidden bg-white">
      {/* Left Section - Animated Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-[#048AAD] via-[#048AAD]/90 to-[#01CC54]">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          {/* Floating Circles */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-white/10"
              style={{
                width: Math.random() * 200 + 100,
                height: Math.random() * 200 + 100,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                x: [0, 20, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 py-16 text-white">
          {/* Logo with Animation */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <img
              src={bluTecPingLogo}
              alt="BluTec PING"
              className="h-20 mb-4 filter brightness-0 invert"
            />
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-5xl font-extrabold mb-4 leading-tight"
          >
            Welcome to
            <br />
            <span className="text-white/90">BluTec PING</span>
          </motion.h1>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl text-white/90 mb-12 max-w-md"
          >
            Scale your WhatsApp business communications with AI-powered automation and real-time analytics.
          </motion.p>

          {/* Feature Highlights */}
          <div className="space-y-4">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  className="flex items-start gap-4 group"
                >
                  <motion.div
                    className="p-3 rounded-xl bg-white/20 backdrop-blur-sm group-hover:bg-white/30 transition-colors"
                    whileHover={{ scale: 1.05, rotate: 5 }}
                  >
                    <Icon size={24} className="text-white" />
                  </motion.div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-white/80 text-sm">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Decorative Sparkles */}
          <motion.div
            className="absolute bottom-20 right-20"
            animate={{
              rotate: [0, 360],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "linear",
            }}
          >
            <Sparkles size={80} className="text-white/20" />
          </motion.div>
        </div>
      </div>

      {/* Right Section - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-6 lg:px-12 py-10 sm:py-16 overflow-y-auto bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <motion.img
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              src={bluTecPingLogo}
              alt="BluTec PING"
              className="h-16 mx-auto mb-4"
            />
            <h1 className="text-3xl font-extrabold text-[#01CC54] mb-2">
              Welcome Back
            </h1>
            <p className="text-gray-600">
              Sign in to your WhatsApp growth dashboard
            </p>
          </div>

          {/* Desktop Welcome */}
          <div className="hidden lg:block mb-8">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-2">
              Welcome Back
            </h1>
            <p className="text-gray-600">
              Sign in to continue to your dashboard
            </p>
          </div>

          {/* Login Card */}
          <div className="relative rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-[#048AAD] via-[#048AAD]/90 to-[#01CC54]" />
            <div className="p-7">
              {/* Error */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-5 rounded-xl border border-red-300 bg-red-50 text-red-700 p-3"
                >
                  <div className="flex items-start gap-2">
                    <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">{error}</p>
                      {shouldShowForgotPassword() && (
                        <button
                          onClick={() => {
                            if (!form.email) {
                              toast.error(
                                "Please enter your email to reset password"
                              );
                              return;
                            }
                            navigate("/forget-password", {
                              state: { email: form.email },
                            });
                          }}
                          className="mt-1 text-sm text-[#01CC54] hover:text-[#01CC54]/80 underline underline-offset-2"
                          type="button"
                        >
                          Forgot Password?
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Security chip */}
              <div className="flex items-center justify-center mb-6">
                <motion.span
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="inline-flex items-center gap-2 rounded-full border border-[#01CC54]/20 bg-[#01CC54]/10 px-3 py-1.5 text-[#01CC54] text-xs font-medium"
                >
                  <Shield size={14} />
                  Secure authentication
                </motion.span>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <label
                    htmlFor="email"
                    className="mb-1 block text-sm font-medium text-gray-600"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@company.com"
                    autoComplete="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="block w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-base text-gray-900 placeholder-gray-500 shadow-sm outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <label
                    htmlFor="password"
                    className="mb-1 block text-sm font-medium text-gray-600"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      value={form.password}
                      onChange={handleChange}
                      required
                      className="block w-full rounded-xl border border-gray-200 bg-white px-4 py-3 pr-12 text-base text-gray-900 placeholder-gray-500 shadow-sm outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#01CC54] transition-colors"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </motion.div>

                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  type="submit"
                  disabled={isLoading}
                  className={`w-full rounded-xl py-3 text-lg font-semibold text-white bg-gradient-to-r from-[#048AAD] to-[#048AAD]/90 shadow-lg transition-all hover:shadow-xl hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#048AAD]/30 ${
                    isLoading ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  whileHover={!isLoading ? { scale: 1.02 } : {}}
                  whileTap={!isLoading ? { scale: 0.98 } : {}}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                      <span>Signing in...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <LogIn size={18} />
                      <span>Sign In</span>
                    </div>
                  )}
                </motion.button>
              </form>

              {/* Divider */}
              <div className="flex items-center my-6">
                <hr className="flex-grow border-t border-gray-200" />
                <span className="mx-3 text-gray-500 text-sm">OR</span>
                <hr className="flex-grow border-t border-gray-200" />
              </div>

              {/* Google */}
              <div className="flex justify-center mt-3 mb-2">
                <GoogleOAuthProvider clientId={googleClientId}>
                  <GoogleLogin
                    onSuccess={handleGoogleLogin}
                    onError={() => toast.error("Google Login Failed")}
                    theme="outline"
                    text="continue_with"
                    width="290px"
                    shape="pill"
                    size="large"
                    logo_alignment="center"
                    disabled={isGoogleLoading}
                  />
                </GoogleOAuthProvider>
              </div>

              {/* Signup */}
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600">
                  Don't have an account?{" "}
                  <a
                    href="/signup"
                    className="font-semibold text-[#01CC54] hover:text-[#01CC54]/80 underline underline-offset-4 transition-colors"
                  >
                    Sign up
                  </a>
                </p>
                <p className="text-gray-500 text-sm py-2">
                  Trouble signing in? Contact{" "}
                  <a
                    href="mailto:sales@blutec.ai"
                    className="text-[#01CC54] hover:text-[#01CC54]/80 underline underline-offset-2 font-semibold transition-colors"
                  >
                    sales@blutec.ai
                  </a>
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
